import React, { useEffect, useState } from 'react';
import { Popover, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';

import UserAvatar from './UserAvatar';
import { userLabel } from './UserChip';
import { getUsersByIds } from '../../services/users';
import { isAdmin } from '../../utils';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { onSessionChange } from '../../utils/session';

const { Text } = Typography;

const ROLE_TAG_META = {
	admin: { color: 'gold', label: 'Admin' },
	'attach-admin': { color: 'blue', label: 'Attachments admin' },
	reviewer: { color: 'green', label: 'Reviewer' },
};

/** Platform-role tags, one vocabulary everywhere roles render. */
export function UserRoleTags({ roles }) {
	if (!roles?.length) return null;
	return (
		<>
			{roles.map(role => {
				const meta = ROLE_TAG_META[role];
				return (
					<Tag key={role} color={meta?.color}>
						{meta?.label || role}
					</Tag>
				);
			})}
		</>
	);
}

// One resolution per user id per session: hover cards appear in lists (grant
// rows, avatar stacks), and every instance sharing an id shares the promise.
const resolveCache = new Map();
// The projection is what THIS account may see of someone: re-ask per account.
onSessionChange(() => resolveCache.clear());

const resolveUser = id => {
	if (resolveCache.has(id)) return resolveCache.get(id);
	const promise = getUsersByIds([id])
		.then(list => list?.[0] || null)
		.catch(() => {
			// A transient failure must not poison the session — retry next mount.
			resolveCache.delete(id);
			return null;
		});
	resolveCache.set(id, promise);
	return promise;
};

/**
 * The contact-card popover body: who this is, at a glance. Works from any
 * user projection — public ({_id, name fields, avatar, email}) or richer
 * (roles, createdAt) — and simply omits what it wasn't given. Platform
 * admins additionally get a jump into user administration.
 */
export function UserHoverCard({ user }) {
	if (!user) return <Text type="secondary">Unknown user</Text>;

	return (
		<div className="user-hover-card" style={{ maxWidth: 280 }}>
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
				<UserAvatar user={user} size={40} />
				<div style={{ minWidth: 0 }}>
					<div style={{ fontWeight: 600 }}>{userLabel(user)}</div>
					{user.email ? (
						<div>
							<Text type="secondary" style={{ wordBreak: 'break-all' }}>
								{user.email}
							</Text>
						</div>
					) : null}
					{user.username ? (
						<div>
							<Text type="secondary">@{user.username}</Text>
						</div>
					) : null}
				</div>
			</div>
			{user.roles?.length ? (
				<div style={{ marginTop: 8 }}>
					<UserRoleTags roles={user.roles} />
				</div>
			) : null}
			{user.createdAt ? (
				<div style={{ marginTop: 8 }}>
					<Text type="secondary">Member since {formatDisplayDate(user.createdAt)}</Text>
				</div>
			) : null}
			{isAdmin() && user.email ? (
				<div style={{ marginTop: 8 }}>
					<Link to={`/admin/users?q=${encodeURIComponent(user.email)}`}>
						Open in user administration
					</Link>
				</div>
			) : null}
		</div>
	);
}

/**
 * Inline clickable user reference: avatar + name wrapped in the contact-card
 * popover. Give it a `user` record when one is on hand; with only a `userId`
 * it resolves the public projection through the batch users endpoint
 * (cached per id for the session). Hover opens on desktop; the click
 * trigger covers touch, where hover doesn't exist.
 *
 *   <UserRef user={grant.user} userId={grant.principalId} />
 *   <UserRef user={member} size={24} showName={false} />   // avatar-only
 *   <UserRef user={u}>Custom label</UserRef>
 */
export default function UserRef({
	user,
	userId,
	size = 24,
	showAvatar = true,
	showName = true,
	children,
}) {
	const id = normalizeId(user?._id) || normalizeId(userId);
	const needsFetch = !user && !!id;
	const [fetched, setFetched] = useState(null);

	useEffect(() => {
		if (!needsFetch) return undefined;
		let alive = true;
		resolveUser(id).then(resolved => {
			if (alive) setFetched(resolved);
		});
		return () => {
			alive = false;
		};
	}, [needsFetch, id]);

	const resolved = user || fetched;
	// Unresolvable ids stay legible as the raw id — same fallback the sharing
	// surfaces used before this component existed.
	const label = children ?? (resolved ? userLabel(resolved) : id || 'Unknown user');

	return (
		<Popover
			content={<UserHoverCard user={resolved} />}
			trigger={['hover', 'click']}
			placement="top"
			mouseEnterDelay={0.25}
		>
			<span
				className="user-ref"
				role="button"
				tabIndex={0}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 8,
					cursor: 'pointer',
					maxWidth: '100%',
				}}
			>
				{showAvatar ? <UserAvatar user={resolved} size={size} /> : null}
				{showName ? <span className="user-ref__name">{label}</span> : null}
			</span>
		</Popover>
	);
}
